import { Injectable } from '@angular/core';
import {
  CreateNarcoticPrescriptionBlockDto,
    CreateOrUpdateNarcoticPrescriptionBlockDetailDto,
    GetNarcoticPrescriptionBlockDetailDto,
    GetNarcoticPrescriptionBlockDto,
    GetNarcoticPrescriptionBlockEmploymentDto,
    NarcoticPrescriptionBlockDto,
    NarcoticPrescriptionBlockSearchInputDto,
    NarcoticPrescriptionBlockSearchOutputDto,
    NarcoticPrescriptionBlockServiceProxy,
} from '@shared/service-proxies/service-proxies';
import { PersonDossierService } from './person-dossier.service';
import { map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class NarcoticPrescriptionBlockService {

    private blockDetails: GetNarcoticPrescriptionBlockDetailDto[] = [];

    constructor(
        private readonly narcoticPrescriptionBlockServiceProxy: NarcoticPrescriptionBlockServiceProxy,
        private readonly personDossierService: PersonDossierService
    ) {}

    public GetNarcoticPrescriptionBlocksForPerson(
        sorting: string,
        filter: string,
        skip: number,
        count: number
    ): Observable<NarcoticPrescriptionBlockSearchOutputDto> {
        const personId = this.personDossierService.GetId();
        return this.narcoticPrescriptionBlockServiceProxy.search(
            new NarcoticPrescriptionBlockSearchInputDto({
                personId: personId,
                filter: filter,
                sorting: sorting,
                skipCount: skip,
                maxResultCount: count,
            })
        );
    }

    public Get(id: number): Observable<GetNarcoticPrescriptionBlockDto> {
      return this.narcoticPrescriptionBlockServiceProxy.get(id).pipe(tap(x => {
        this.blockDetails = x.blockDetails;
      }));
    }

    public Create(dto: CreateNarcoticPrescriptionBlockDto): Observable<number> {
      dto.personId = this.personDossierService.GetId();
      return this.narcoticPrescriptionBlockServiceProxy.create(dto);
    }

    public Update(id: number, dto: NarcoticPrescriptionBlockDto): Observable<void> {
      return this.narcoticPrescriptionBlockServiceProxy.update(id, dto);
    }

    public Delete(id: number): Observable<void> {
      return this.narcoticPrescriptionBlockServiceProxy.delete(id);
    }

    public GetCurrentBlockDetails(): GetNarcoticPrescriptionBlockDetailDto[] {
      return this.blockDetails;
    }

    public ResetBlockDetails(): void {
      this.blockDetails = [];
    }

    public CreateDetailBlock(narcoticPrescriptionBlockId: number, dto: CreateOrUpdateNarcoticPrescriptionBlockDetailDto): Observable<number> {
      const dtos = [dto];
      return this.CreateDetailBlocks(narcoticPrescriptionBlockId, dtos);
    }

    public CreateDetailBlocks(narcoticPrescriptionBlockId: number, dtos: CreateOrUpdateNarcoticPrescriptionBlockDetailDto[]): Observable<number> {
      return this.narcoticPrescriptionBlockServiceProxy.createDetailBlocks(narcoticPrescriptionBlockId, dtos).pipe(tap(x => {
        this.blockDetails = x as GetNarcoticPrescriptionBlockDetailDto[];
      }),
      map(x => {
        return Math.max(...x.map(d => d.id)); // Return the id, it has the highest id
      }));
    }

    public UpdateDetailBlock(narcoticPrescriptionBlockId: number, id: number, dto: CreateOrUpdateNarcoticPrescriptionBlockDetailDto): Observable<void> {
      return this.narcoticPrescriptionBlockServiceProxy.updateDetailBlock(narcoticPrescriptionBlockId, id, dto).pipe(tap(x => {
        this.blockDetails = x as GetNarcoticPrescriptionBlockDetailDto[];
      }),
      map(x => null));
    }

    public DeleteDetailBlock(narcoticPrescriptionBlockId: number, id: number): Observable<void> {
      return this.narcoticPrescriptionBlockServiceProxy.deleteDetailBlock(narcoticPrescriptionBlockId, id).pipe(tap(x => {
        this.blockDetails = x as GetNarcoticPrescriptionBlockDetailDto[];
      }),
      map(x => null));
    }

    public GetEmployments(): Observable<GetNarcoticPrescriptionBlockEmploymentDto[]> {
      return this.narcoticPrescriptionBlockServiceProxy.getEmploymentsForPerson(this.personDossierService.GetId());
    }

    public ValidateFirstRecipeNumber(firstRecipeNumber: string): {} {
      const number = parseInt(firstRecipeNumber);
      if (isNaN(number)) {
          return { 'invalidNumber': true };
      }

      // The number must end with either 01, 26, 51 or 76
      if (number % 25 != 1) {
          return { 'invalidNarcoticPrescriptionBlockFirstRecipeNumber': true };
      }

      return null;
    }

    public CalculateLastRecipeNumber(firstRecipeNumber: number): number {
      return firstRecipeNumber + 24;
    }
}
